import { Timestamp } from 'firebase/firestore';

// ============================================================================
// USER TYPES
// ============================================================================

export interface UserProfile {
  id: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  phoneNumber: string | null;
  systemRole?: 'super_admin' | 'support' | null;
  createdAt: Timestamp;
  lastLoginAt: Timestamp;
  preferences: UserPreferences;
  metadata: UserMetadata;
  /** Per-user legal acceptance record. Absent until the user accepts. */
  legal?: UserLegalAcceptance | null;
}

/** Per-user click-through acceptance, written by AuthContext.acceptLegal(). */
export interface UserLegalAcceptance {
  /** Matches CURRENT_LEGAL_VERSION at accept time; drives the re-prompt gate. */
  eulaVersion: string;
  acceptedAt: Timestamp;
  /** { docKey: version } snapshot of every document accepted. */
  acceptedDocs: Record<string, string>;
  /**
   * Server-observed client IP at accept time. Stamped by the
   * POST /organizations/accept-legal endpoint; null when only the client-side
   * fallback ran (the browser can't observe its own egress IP).
   */
  ip?: string | null;
  /** Server-observed User-Agent string at accept time (endpoint path only). */
  userAgent?: string | null;
  /**
   * { docKey: 'sha256:<hex>' } of the exact text the user accepted, so a
   * material text change can be detected. Empty when hashing was unavailable.
   */
  contentHashes?: Record<string, string>;
}

/** Org-level Master-Services-Agreement binding, written by an owner/admin. */
export interface OrgLegalAcceptance {
  /** Matches ORG_LEGAL_VERSION at accept time. */
  msaVersion: string;
  acceptedByUid: string;
  acceptedByEmail?: string | null;
  acceptedAt: Timestamp;
  /** Server-observed client IP at accept time (endpoint path only). */
  ip?: string | null;
}

export interface UserPreferences {
  theme: 'dark' | 'light';
  language: string;
  /**
   * Preferred IANA time zone (e.g. `America/New_York`) for rendering all dates
   * in the portal + report exports. Absent or `'auto'` → follow the browser
   * (then org default, then UTC). See `resolveTimeZone` in `@simcheck/shared-types`.
   */
  timezone?: string;
  notifications: boolean;
}

export interface UserMetadata {
  emailVerified: boolean;
  provider: string; // 'email' | 'google.com' | etc.
  lastOrganizationId: string | null; // For quick resume
}

// ============================================================================
// ORGANIZATION TYPES
// ============================================================================

export interface Organization {
  id: string;
  name: string;
  slug: string;
  ownerId: string;
  logoURL: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  settings: OrganizationSettings;
  subscription: OrganizationSubscription;
  /** Org-level MSA/Terms acceptance, recorded when an owner/admin accepts. */
  legal?: OrgLegalAcceptance | null;
  /** Registration approval (free-trial vetting). Absent === approved. */
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  /** Denormalized count of org-owned edge devices. */
  deviceCount?: number;
}

export interface OrganizationSettings {
  allowSelfSignup: boolean;
  defaultRole: Extract<UserRole, 'member' | 'viewer'>;
  requireEmailVerification: boolean;
  allowedDomains: string[]; // For auto-join
  isDemoOrg?: boolean;      // Can see demo-tier devices
  hasLabAccess?: boolean;   // Can see lab-tier devices

  /**
   * Per-tenant feature flags. Mirrors `OrganizationFeatures` in
   * `@simcheck/shared-types`. Read here so the React frontend can gate UI
   * elements (admin nav, settings panels) without round-tripping to the API.
   */
  features?: {
    /**
     * Kill-switch for the lpac-over-AT consumer-RSP path. Default (undefined /
     * true) → lpac path active. Explicit `false` disables it for this org.
     */
    esimLpacProvisioning?: boolean;

    /**
     * Marks an org as a QA-automation tenant. Default → off. Explicit `true`
     * unlocks the /admin/qa-automation page + cross-tier primitive visibility
     * + FlowValidationService primitive-reference carve-out (read in the
     * backend via `isQaAutomationTenant`). Set per-org via /admin/organizations.
     */
    qaAutomation?: boolean;
    /**
     * Record call audio on automated voice tests as a `voice_wav` artifact.
     * Default (undefined) → on; only an explicit `false` opts the org out.
     * Never applies to interactive TLAM calls.
     */
    voiceCallRecording?: boolean;
    /**
     * Grants access to the super-admin shared eSIM inventory (profiles with
     * `shared: true`). Default → off; only an explicit `true` grants it. Read
     * via `canOrgUseSharedEsims`. Set per-org via /admin/organizations.
     */
    canUseSharedEsims?: boolean;
  };

  /**
   * Org-level security policy. Mirrors `OrganizationSecuritySettings` in
   * `@simcheck/shared-types`. `mfa.mode`: 'off' | 'allowed' | 'required'.
   * Read here so the client 2FA gate can decide whether to challenge.
   */
  security?: {
    mfa?: {
      mode: 'off' | 'allowed' | 'required';
      graceDays?: number;
      requiredSince?: any; // Firestore Timestamp on the wire
    };
  };

  /**
   * Org-level report settings. Mirrors `OrganizationReportsSettings` in
   * `@simcheck/shared-types`. `defaultTimezone` is the IANA zone used for
   * members who haven't set a personal one — read by `useUserTimezone` for
   * on-screen rendering and by the backend for export/delivery bucketing.
   */
  reports?: {
    defaultTimezone?: string;
  };
}

export interface OrganizationSubscription {
  // Open union: known sellable + trial ids plus `custom_<slug>` enterprise clones.
  plan: 'starter' | 'prototyper' | 'business' | 'enterprise' | 'trial' | (string & {});
  status: 'active' | 'trial' | 'expired' | 'past_due' | 'canceled';
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  billingPeriodStart: Timestamp;
  billingPeriodEnd: Timestamp;
  /** Token-billing: denormalized current balance (written with each ledger entry). */
  tokenBalance?: number;
  /** How the subscription is paid: self-serve card, admin wire invoice (Wise), legacy Stripe invoice, or manual. */
  billingMethod?: 'stripe_card' | 'wire' | 'invoice' | 'manual';
  /** Token add-ons: non-resetting balance from one-time purchases (drawn after the monthly bucket). */
  purchasedTokenBalance?: number;
  /** Post-paid overage allowed (billed at end of period). */
  allowOverage?: boolean;
  /** Recurring add-ons attached to the org's Stripe subscription. */
  recurringAddons?: Array<{ addonId: string; stripeItemId: string; tokens?: number; quantity?: number }>;
}

// ============================================================================
// ORGANIZATION MEMBER TYPES
// ============================================================================

export type UserRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface OrganizationMember {
  userId: string;
  role: UserRole;
  joinedAt: Timestamp;
  invitedBy: string | null;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  lastSeenAt: Timestamp;
}

export interface OrganizationMembership {
  organizationId: string;
  role: UserRole;
  organizationName: string;
  organizationSlug: string;
  organizationLogoURL: string | null;
  joinedAt: Timestamp;
  isFavorite: boolean;
}

// ============================================================================
// INVITATION TYPES
// ============================================================================

export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface Invitation {
  id: string;
  organizationId: string;
  organizationName: string;
  email: string;
  role: Exclude<UserRole, 'owner'>; // Cannot invite as owner
  invitedBy: string;
  invitedByName: string;
  status: InvitationStatus;
  token: string;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  acceptedAt: Timestamp | null;
}

// ============================================================================
// PERMISSION TYPES
// ============================================================================

export type Permission =
  // Organization permissions
  | 'org:update-settings'
  | 'org:manage-billing'
  | 'org:delete'
  
  // User management permissions
  | 'users:invite'
  | 'users:remove'
  | 'users:change-role'
  
  // Flow permissions
  | 'flows:view-all'
  | 'flows:create'
  | 'flows:edit-own'
  | 'flows:edit-all'
  | 'flows:delete-own'
  | 'flows:delete-all'
  
  // Execution permissions
  | 'executions:view-all'
  | 'executions:start'
  | 'executions:stop-own'
  | 'executions:stop-all'
  
  // Modem permissions
  | 'modems:view-all'
  | 'modems:manage';

// Permission matrix based on roles
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  owner: [
    // Organization
    'org:update-settings',
    'org:manage-billing',
    'org:delete',
    // Users
    'users:invite',
    'users:remove',
    'users:change-role',
    // Flows
    'flows:view-all',
    'flows:create',
    'flows:edit-own',
    'flows:edit-all',
    'flows:delete-own',
    'flows:delete-all',
    // Executions
    'executions:view-all',
    'executions:start',
    'executions:stop-own',
    'executions:stop-all',
    // Modems
    'modems:view-all',
    'modems:manage',
  ],
  admin: [
    // Organization
    'org:update-settings',
    // Users
    'users:invite',
    'users:remove',
    'users:change-role',
    // Flows
    'flows:view-all',
    'flows:create',
    'flows:edit-own',
    'flows:edit-all',
    'flows:delete-own',
    'flows:delete-all',
    // Executions
    'executions:view-all',
    'executions:start',
    'executions:stop-own',
    'executions:stop-all',
    // Modems
    'modems:view-all',
    'modems:manage',
  ],
  member: [
    // Flows
    'flows:view-all',
    'flows:create',
    'flows:edit-own',
    'flows:delete-own',
    // Executions
    'executions:view-all',
    'executions:start',
    'executions:stop-own',
    // Modems
    'modems:view-all',
  ],
  viewer: [
    // Flows
    'flows:view-all',
    // Executions
    'executions:view-all',
    // Modems
    'modems:view-all',
  ],
};

// Helper function to check if a role has a permission
export function hasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

// Helper function to check if user can edit a resource
export function canEditResource(
  userRole: UserRole,
  userId: string,
  resourceCreatorId: string
): boolean {
  // Owner and admin can edit any resource
  if (userRole === 'owner' || userRole === 'admin') {
    return true;
  }
  // Members can only edit their own resources
  if (userRole === 'member') {
    return userId === resourceCreatorId;
  }
  // Viewers cannot edit anything
  return false;
}

// Helper function to check if user can delete a resource
export function canDeleteResource(
  userRole: UserRole,
  userId: string,
  resourceCreatorId: string
): boolean {
  return canEditResource(userRole, userId, resourceCreatorId);
}

